import { 
    ValidateArchiveAndCaseResult, 
    validateCase
} from '../../../services/api/dataServiceOutlookQuickSave';

/**
 * Interface for case validation callbacks
 */
interface CaseValidationCallbacks {
    onValidationStart: () => void;
    onValidationEnd: () => void;
    onValidationSuccess: (validation: ValidateArchiveAndCaseResult) => void;
    onValidationFailed: (validation: ValidateArchiveAndCaseResult, errorMessage: string) => void;
    onShowRecordTypes: (show: boolean) => void;
    onLoadCategories: (archiveRecno: number) => Promise<void>;
    onError: (errorMessage: string) => void;
}

/**
 * Helper function to handle successful case validation
 */
export const handleSuccessfulCaseValidation = async (
    validation: ValidateArchiveAndCaseResult,
    selectedArchiveRecno: number,
    callbacks: Pick<CaseValidationCallbacks, 'onValidationSuccess' | 'onShowRecordTypes' | 'onLoadCategories'>
) => {
    callbacks.onValidationSuccess(validation);
    
    // If validation passes and record types are required, enable record type dropdown
    if (validation.CaseIsValid && validation.ArchiveIsValid && validation.IsRecordType) {
        callbacks.onShowRecordTypes(true);
    }
    
    // Reload categories after successful validation if needed
    await callbacks.onLoadCategories(selectedArchiveRecno);
};

/**
 * Helper function to handle failed case validation
 */
export const handleFailedCaseValidation = (
    validation: ValidateArchiveAndCaseResult,
    onValidationFailed: (validation: ValidateArchiveAndCaseResult, errorMessage: string) => void
) => {
    const errorMessage = validation.CaseValidationMessage || validation.ArchiveValidationMessage || 'Case validation failed';
    onValidationFailed(validation, errorMessage);
};

/**
 * Main function to validate a case with an archive
 */
export const validateCaseWithArchive = async (
    archiveRecno: number,
    caseReno: number,
    callbacks: CaseValidationCallbacks
) => {
    callbacks.onValidationStart();
    try {
        const validation = await validateCase(archiveRecno, caseReno);
        
        if (!validation.CaseIsValid || !validation.ArchiveIsValid) {
            handleFailedCaseValidation(validation, callbacks.onValidationFailed);
        } else {
            await handleSuccessfulCaseValidation(
                validation, 
                archiveRecno, 
                callbacks
            );
        }
    } catch (err) {
        callbacks.onError(err instanceof Error ? err.message : 'Failed to validate case');
    } finally {
        callbacks.onValidationEnd();
    }
};
